import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface ArticleLink {
  name: string;
  path: string;
}

const seoArticles: ArticleLink[] = [
  { name: 'Budget Finds Under $20', path: '/pandabuy-sheet-budget-finds' },
  { name: 'Sneaker Discovery Guide', path: '/pandabuy-sheet-sneaker-guide' },
  { name: 'Streetwear Essentials', path: '/pandabuy-sheet-streetwear-essentials' },
  { name: 'Trending TikTok Finds', path: '/pandabuy-sheet-trending-tiktok' },
  { name: 'Luxury Style Dupes', path: '/pandabuy-sheet-luxury-dupes' },
  { name: 'Accessories Guide', path: '/pandabuy-sheet-accessories-guide' },
  { name: 'Seasonal Picks 2026', path: '/pandabuy-sheet-seasonal-picks' },
  { name: 'Quality & Sizing Guide', path: '/pandabuy-sheet-quality-guide' },
  { name: 'Beginner Tips', path: '/pandabuy-sheet-beginner-tips' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);
  const articlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (articlesRef.current && !articlesRef.current.contains(e.target as Node)) {
        setArticlesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-background-50/70 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap"
          >
            pandabuy<span className="text-primary-500">.</span>bar
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-medium">
              Home
            </Link>
            <a href="#categories" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-medium">
              Categories
            </a>

            {/* SEO Articles Dropdown */}
            <div className="relative" ref={articlesRef}>
              <button
                onClick={() => setArticlesOpen(!articlesOpen)}
                className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                SEO Articles
                <span className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 ${articlesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>
              {articlesOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 w-56 z-30">
                  {seoArticles.map((a) => (
                    <Link
                      key={a.path}
                      to={a.path}
                      onClick={() => setArticlesOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      {a.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/pandabuy-sheet-ultimate-guide" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-medium">
              Shopping Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 font-medium hover:bg-background-100 rounded-lg mx-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500"><i className="ri-home-4-line"></i></span>
              Home
            </Link>
            <a
              href="#categories"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 font-medium hover:bg-background-100 rounded-lg mx-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500"><i className="ri-grid-line"></i></span>
              Categories
            </a>
            <Link
              to="/pandabuy-sheet-ultimate-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 font-medium hover:bg-background-100 rounded-lg mx-2"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500"><i className="ri-book-open-line"></i></span>
              Shopping Guide
            </Link>

            {/* Mobile SEO Articles */}
            <div className="mx-2">
              <button
                onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                className="flex items-center justify-between w-full gap-3 px-4 py-2.5 text-sm text-foreground-700 font-medium hover:bg-background-100 rounded-lg cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500"><i className="ri-article-line"></i></span>
                  SEO Articles
                </span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileArticlesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>
              {mobileArticlesOpen && (
                <div className="ml-8 mt-1 space-y-0.5 border-l-2 border-background-200 pl-4">
                  {seoArticles.map((a) => (
                    <Link
                      key={a.path}
                      to={a.path}
                      onClick={() => { setMobileOpen(false); setMobileArticlesOpen(false); }}
                      className="block py-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {a.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Category Quick Links */}
            <div className="px-4 pt-3 pb-1">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Quick Categories</p>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-1.5 text-xs text-foreground-600 bg-background-100 border border-background-200 rounded-full hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="px-4 pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer w-full justify-center"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}